import React, { createContext, useContext, useState, useEffect } from "react";

export const CreateContext = createContext();

export const useAppContext = () => useContext(CreateContext);

const Context = ({ children }) => {
  const [toggle, setToggle] = useState(true);
  const [search, setSearch] = useState(true);
  const [mobile, setMobile] = useState(true);
  const [smallMobileMenu, setsmallMobileMenu] = useState(true);
  const [pricing, setPricing] = useState(true);
  const [pricingTwo, setPricingTwo] = useState(true);
  const [pricingThree, setPricingThree] = useState(true);
  const [pricingFour, setPricingFour] = useState(true);
  
  const [isLightTheme, setLightTheme] = useState(true);

  // 4-way theme mode: "light" | "dark" | "gray" | "system". Extends the original light/dark-only
  // mechanism (same localStorage key, "histudy-theme", and the same document.body class strategy)
  // rather than replacing it -- the existing sitewide dark-mode toggle button keeps working
  // unmodified via isLightTheme/toggleTheme below, now expressed as a special case of themeMode.
  //
  // "system" is stored literally as the string "system" (never resolved to light/dark before
  // saving) so a later OS theme change is picked up live by the matchMedia listener below, exactly
  // as the task requires -- we don't "bake in" whatever the OS happened to prefer at save time.
  const [themeMode, setThemeModeState] = useState("system");

  // Applies the correct body class for a given resolved mode. Only one of .active-dark-mode /
  // .active-gray-mode is ever present at once. "system" resolves live against matchMedia instead
  // of writing its own class.
  const applyThemeClass = (mode, systemPrefersDark) => {
    const body = document.body;
    body.classList.remove("active-dark-mode", "active-gray-mode");

    if (mode === "dark") {
      body.classList.add("active-dark-mode");
    } else if (mode === "gray") {
      body.classList.add("active-gray-mode");
    } else if (mode === "system") {
      if (systemPrefersDark) body.classList.add("active-dark-mode");
    }
    // "light" (or system resolving to light): no class.
  };

  // Load the persisted choice once on mount. Default is "system" -- an explicit stored
  // light/dark/gray choice always wins, but a first-time visitor (or cleared storage)
  // should get the platform's own preference rather than a forced light theme.
  useEffect(() => {
    const stored = localStorage.getItem("histudy-theme");
    const validModes = ["light", "dark", "gray", "system"];
    const initialMode = validModes.includes(stored) ? stored : "system";

    const systemPrefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const resolvedDark = initialMode === "dark" || (initialMode === "system" && systemPrefersDark);

    setThemeModeState(initialMode);
    // isLightTheme tracks the *resolved* visual state (what toggleTheme's plain light/dark
    // flip should undo), not the literal mode string -- "system" resolving to dark must count
    // as not-light, or the sitewide toggle button would silently no-op the first time it's
    // clicked while system mode happens to be showing dark.
    setLightTheme(!resolvedDark && initialMode !== "gray");

    applyThemeClass(initialMode, systemPrefersDark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live-follow the OS theme while in "system" mode -- if the user (or their OS schedule)
  // flips light/dark at the system level, the site updates without needing a manual re-toggle.
  useEffect(() => {
    if (themeMode !== "system" || !window.matchMedia) return undefined;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      applyThemeClass("system", e.matches);
      setLightTheme(!e.matches);
    };

    // Safari <14 only supports the deprecated addListener/removeListener pair.
    if (mql.addEventListener) mql.addEventListener("change", handleChange);
    else mql.addListener(handleChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handleChange);
      else mql.removeListener(handleChange);
    };
  }, [themeMode]);

  // Sets the theme mode (used by the new 4-way control in Settings), persists the *choice* (not
  // a resolved value) to localStorage, and applies the corresponding body class immediately.
  const setThemeMode = (mode) => {
    if (!["light", "dark", "gray", "system"].includes(mode)) return;

    const systemPrefersDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    const resolvedDark = mode === "dark" || (mode === "system" && systemPrefersDark);

    setThemeModeState(mode);
    setLightTheme(!resolvedDark && mode !== "gray");
    localStorage.setItem("histudy-theme", mode);

    applyThemeClass(mode, systemPrefersDark);
  };

  // Kept for every existing call site (the sitewide dark-mode toggle button, etc.) -- a simple
  // light/dark flip that never touches gray or system. Understands those two states insofar as
  // toggling away from either one always lands back on plain light/dark.
  const toggleTheme = () => {
    setThemeMode(isLightTheme ? "dark" : "light");
  };

  return (
    <CreateContext.Provider
      value={{
        toggle,
        setToggle,
        mobile,
        setMobile,
        smallMobileMenu,
        setsmallMobileMenu,
        search,
        setSearch,
        pricing,
        setPricing,
        pricingTwo,
        setPricingTwo,
        pricingThree,
        setPricingThree,
        pricingFour,
        setPricingFour,
        isLightTheme,
        setLightTheme,
        toggleTheme,
        themeMode,
        setThemeMode,
      }}
    >
      {children}
    </CreateContext.Provider>
  );
};

export default Context;
